/*!

=========================================================
* Black Dashboard React v1.2.2
=========================================================

* Product Page: https://www.creative-tim.com/product/black-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/black-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import React from "react";
// nodejs library that concatenates classes
import classNames from "classnames";
import { LOGIN_RESET
} from '../../action/userAction'
// reactstrap components
import {
  Button,
  Collapse,
  DropdownToggle,
  DropdownMenu,
  UncontrolledDropdown,
  Input,
  // InputGroup,
  NavbarBrand,
  Navbar,
  Nav,
  Container,
  Modal,
  NavbarToggler,
  ModalHeader,
} from "reactstrap";
import { useDispatch } from "react-redux";
import axios from "axios";
import ChangePasswordModal from "components/ChangePasswordModal";
import "./AdminNavbar.css";

// ---------------------------------------------------------------------------
// 使用者圓標（v2.32.0）
// ---------------------------------------------------------------------------

/** 顯示名稱 → 圓標文字。中文取最後 1~2 字，英文取首字母。 */
function getInitials(name) {
  const n = (name || "").trim();
  if (!n) return "?";
  // 有 CJK 就取最後兩個字（「布萊德屁哥」→「屁哥」比「布萊」好認）
  if (/[\u4e00-\u9fff]/.test(n)) return n.slice(-2);
  const parts = n.split(/[\s._-]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return n.slice(0, 2).toUpperCase();
}

/**
 * 帳號 → 固定顏色。
 * 用帳號名雜湊而不是隨機，這樣**同一個人每次登入都是同一個顏色** ——
 * 顏色會變的話就失去「一眼認出自己」的作用了。
 */
function getAvatarColor(username) {
  const palette = [
    "#667eea", "#2dce89", "#11cdef", "#fb6340",
    "#f5365c", "#8965e0", "#ffd600", "#5e72e4",
  ];
  let hash = 0;
  for (let i = 0; i < (username || "").length; i += 1) {
    hash = (hash * 31 + username.charCodeAt(i)) >>> 0;
  }
  return palette[hash % palette.length];
}

function AdminNavbar(props) {
  const dispatch = useDispatch();
  // v2.58.0：一鍵開啟 partner 專區。先同步開一個空白分頁（等 API 回來才開會被瀏覽器當成彈窗擋掉），
  // 拿到 60 秒的通行證再把那個分頁導過去；通行證放在網址 # 後面，不會進伺服器的 log。
  const openPartnerPortal = async () => {
    const win = window.open("about:blank", "_blank");
    try {
      const res = await axios.post("/api/partner-portal/handoff");
      if (res.data?.code === 1 && res.data.url) {
        if (win) {
          win.opener = null;
          win.location.href = res.data.url;
        } else {
          window.location.href = res.data.url;
        }
        return;
      }
      if (win) win.close();
      window.alert(res.data?.msg || "開啟失敗，請稍後再試");
    } catch (e) {
      if (win) win.close();
      window.alert(e.response?.data?.msg || "開啟失敗，請稍後再試");
    }
  };
  const [passwordModalOpen, setPasswordModalOpen] = React.useState(false);

  // ---- 登入者身分（v2.32.0）----
  const displayName = props.user?.display_name || props.user?.username || "";
  const username = props.user?.username || "";
  const roleLabel = props.user?.caps?.manage_users
    ? "最高權限"
    : (props.user?.caps?.view_revenue ? "可看營收" : "");
  const initials = getInitials(displayName);
  const avatarColor = getAvatarColor(username);
  const [collapseOpen, setcollapseOpen] = React.useState(false);
  const [modalSearch, setmodalSearch] = React.useState(false);
  const [color, setcolor] = React.useState("navbar-transparent");
  React.useEffect(() => {
    window.addEventListener("resize", updateColor);
    // Specify how to clean up after this effect:
    return function cleanup() {
      window.removeEventListener("resize", updateColor);
    };
  });
  // function that adds color white/transparent to the navbar on resize (this is for the collapse)
  const updateColor = () => {
    if (window.innerWidth < 993 && collapseOpen) {
      setcolor("bg-white");
    } else {
      setcolor("navbar-transparent");
    }
  };
  // this function opens and closes the collapse on small devices
  const toggleCollapse = () => {
    if (collapseOpen) {
      setcolor("navbar-transparent");
    } else {
      setcolor("bg-white");
    }
    setcollapseOpen(!collapseOpen);
  };
  // this function is to open the Search modal
  const toggleModalSearch = () => {
    setmodalSearch(!modalSearch);
  };
  const handleSidebarControl = () => {
    if (window.innerWidth < 992) {
      if (props.toggleSidebar) {
        props.toggleSidebar();
      }
      return;
    }
    if (props.toggleSidebarVisibility) {
      props.toggleSidebarVisibility();
    }
  };
  const viewportWidth = typeof window !== "undefined" ? window.innerWidth : 1200;
  const isDesktop = viewportWidth >= 992;
  return (
    <>
      <Navbar className={classNames("navbar-absolute", color)} expand="lg">
        <Container fluid>
          <div className="navbar-wrapper">
            <Button className="admin-sidebar-control-btn" color="link" onClick={handleSidebarControl}>
              <i className={classNames("fa mr-1", isDesktop ? "fa-columns" : "fa-bars")} />
              <span className="admin-sidebar-control-text">{isDesktop ? "側欄" : "選單"}</span>
              {isDesktop && (
                <span className={classNames("admin-sidebar-control-badge", props.sidebarVisible ? "on" : "off")}>
                  {props.sidebarVisible ? "顯示中" : "已隱藏"}
                </span>
              )}
            </Button>
            <NavbarBrand className="admin-navbar-brand" href="#pablo" onClick={(e) => e.preventDefault()}>
              {props.brandText}
            </NavbarBrand>
          </div>
          <NavbarToggler onClick={toggleCollapse}>
            <span className="navbar-toggler-bar navbar-kebab" />
            <span className="navbar-toggler-bar navbar-kebab" />
            <span className="navbar-toggler-bar navbar-kebab" />
          </NavbarToggler>
          <Collapse navbar isOpen={collapseOpen}>
            <Nav className="ml-auto" navbar>
              {/* <InputGroup className="search-bar">
                <Button color="link" onClick={toggleModalSearch}>
                  <i className="tim-icons icon-zoom-split" />
                  <span className="d-lg-none d-md-block">Search</span>
                </Button>
              </InputGroup> */}
              {/* <UncontrolledDropdown nav>
                <DropdownToggle
                  caret
                  color="default"
                  data-toggle="dropdown"
                  nav
                >
                  <div className="notification d-none d-lg-block d-xl-block" />
                  <i className="tim-icons icon-sound-wave" />
                  <p className="d-lg-none">Notifications</p>
                </DropdownToggle>
                <DropdownMenu className="dropdown-navbar" right tag="ul">
                  <NavLink tag="li">
                    <DropdownItem className="nav-item">
                      Mike John responded to your email
                    </DropdownItem>
                  </NavLink>
                  <NavLink tag="li">
                    <DropdownItem className="nav-item">
                      You have 5 more tasks
                    </DropdownItem>
                  </NavLink>
                  <NavLink tag="li">
                    <DropdownItem className="nav-item">
                      Your friend Michael is in town
                    </DropdownItem>
                  </NavLink>
                  <NavLink tag="li">
                    <DropdownItem className="nav-item">
                      Another notification
                    </DropdownItem>
                  </NavLink>
                  <NavLink tag="li">
                    <DropdownItem className="nav-item">
                      Another one
                    </DropdownItem>
                  </NavLink>
                </DropdownMenu>
              </UncontrolledDropdown> */}
              {/* v2.32.0：改成一人一帳號之後，右上角要看得出「我現在是誰」。
                  原本是一張所有人共用的預設動漫頭像 —— 那在共用帳號的年代
                  沒差，現在會讓人搞不清楚自己登入的是哪個帳號。 */}
              <UncontrolledDropdown nav>
                <DropdownToggle
                  color="default"
                  nav
                  className="navbar-user-trigger"
                  onClick={(e) => e.preventDefault()}
                >
                  <span className="navbar-user-avatar" style={{ background: avatarColor }}>
                    {initials}
                  </span>
                  <span className="navbar-user-name">{displayName}</span>
                  <i className="fa fa-angle-down navbar-user-caret" />
                </DropdownToggle>
                <DropdownMenu className="dropdown-navbar navbar-user-menu" right tag="ul">
                  <li className="navbar-user-summary">
                    <span className="navbar-user-summary-avatar" style={{ background: avatarColor }}>
                      {initials}
                    </span>
                    <span className="navbar-user-summary-text">
                      <span className="navbar-user-summary-name">{displayName}</span>
                      <span className="navbar-user-summary-meta">
                        {username}{roleLabel ? ` · ${roleLabel}` : ""}
                      </span>
                    </span>
                  </li>
                  <li className="navbar-user-menu-divider" />
                  <li>
                    <button
                      type="button"
                      className="navbar-user-menu-item"
                      onClick={openPartnerPortal}
                    >
                      <i className="fa fa-handshake" />合作夥伴專區
                    </button>
                  </li>
                  <li>
                    <button
                      type="button"
                      className="navbar-user-menu-item"
                      onClick={() => setPasswordModalOpen(true)}
                    >
                      <i className="fa fa-key" />修改密碼
                    </button>
                  </li>
                  <li className="navbar-user-menu-divider" />
                  <li>
                    <button
                      type="button"
                      className="navbar-user-menu-item is-danger"
                      onClick={() => {
                        sessionStorage.clear();
                        dispatch({ type: LOGIN_RESET })
                      }}
                    >
                      <i className="fa fa-sign-out" />登出
                    </button>
                  </li>
                </DropdownMenu>
              </UncontrolledDropdown>
              <li className="separator d-lg-none" />
            </Nav>
          </Collapse>
        </Container>
      </Navbar>
      <ChangePasswordModal
        isOpen={passwordModalOpen}
        toggle={() => setPasswordModalOpen(false)}
      />
      <Modal
        modalClassName="modal-search"
        isOpen={modalSearch}
        toggle={toggleModalSearch}
      >
        <ModalHeader>
          <Input placeholder="SEARCH" type="text" />
          <button
            aria-label="Close"
            className="close"
            onClick={toggleModalSearch}
          >
            <i className="tim-icons icon-simple-remove" />
          </button>
        </ModalHeader>
      </Modal>
      <style>{`
        .navbar .navbar-wrapper .admin-navbar-brand {
          position: relative !important;
          margin-left: 8px !important;
          margin-top: 0 !important;
          text-transform: none;
        }
        .admin-sidebar-control-btn {
          border: 1px solid rgba(122, 145, 189, 0.45) !important;
          border-radius: 9px !important;
          background: linear-gradient(135deg, rgba(51, 73, 114, 0.9), rgba(34, 53, 88, 0.9)) !important;
          color: #e4eeff !important;
          padding: 4px 9px !important;
          font-size: 12px !important;
          line-height: 1.1;
          margin: 0 !important;
          display: inline-flex !important;
          align-items: center;
          gap: 5px;
          box-shadow: 0 6px 14px rgba(10, 18, 34, 0.22);
        }
        .admin-sidebar-control-btn:hover {
          filter: brightness(1.07);
        }
        .admin-sidebar-control-text {
          font-weight: 700;
          letter-spacing: 0.2px;
        }
        .admin-sidebar-control-badge {
          font-size: 10px;
          border-radius: 999px;
          padding: 1px 7px;
          border: 1px solid transparent;
        }
        .admin-sidebar-control-badge.on {
          color: #bff7dd;
          border-color: rgba(47, 191, 95, 0.45);
          background: rgba(47, 191, 95, 0.16);
        }
        .admin-sidebar-control-badge.off {
          color: #ffe1d7;
          border-color: rgba(255, 128, 102, 0.45);
          background: rgba(255, 128, 102, 0.16);
        }
        @media (max-width: 575.98px) {
          .admin-sidebar-control-text {
            display: none;
          }
        }
      `}</style>
    </>
  );
}

export default AdminNavbar;
